import "./TechMarker.css"

import Environment from "../../env/env"
import Technician from "../../modules/technicians/models/technician"

/**
 * A technician on a map, as their own face.
 *
 * Twenty technicians drawn with the same little icon is twenty of nothing: the map tells you
 * that somebody is there but never who, and finding one person means hovering over the lot.
 * A photo is recognised before it is read, so the question "where is Andy" is answered by
 * looking rather than by searching.
 *
 * Without a photo they get their initials on a colour of their own — picked from their id, so
 * it is the same colour on every map, every day. That is still something to recognise them by,
 * which a shared silhouette is not.
 */

/** The pin's point, below the circle, so the marker sits on the position rather than beside it. */
const tailHeight = 9

/** How far the point reaches up behind the circle, so the two read as one shape, not two. */
const tailOverlap = 3

const tailWidth = 7

/**
 * Colours far enough apart to tell two of them apart at a glance, on light tiles and dark.
 *
 * No greens: green is what the ring turns while somebody is reporting, and a green face inside
 * a green ring is a technician whose own colour has been taken away from them on exactly the
 * occasion it matters most.
 */
const palette = [
    "#1b84ff", "#f1416c", "#7239ea", "#f6a000", "#00a3ff",
    "#e8308a", "#6f42c1", "#d6336c", "#5b21b6", "#ea580c",
    "#0369a1", "#b54708"
]

interface TechMarkerOptions {
    /** On a job right now — a red dot, the same thing the busy marker used to say. */
    busy?: boolean
    /** The one being chosen, on the maps where a technician is picked: a ring in the brand blue. */
    selected?: boolean
    /** Diameter of the face. The maps already scale their markers with the zoom. */
    size?: number
    /** Just the face, with no pin around it — for a tooltip, where it is not pointing at anything. */
    plain?: boolean
}

/** The whole marker, ready for `L.marker(position, { icon })`. */
export function techMarkerIcon(L: any, tech: Technician, options?: TechMarkerOptions) {
    const size = options?.size ?? 40
    const height = size + tailHeight

    return L.divIcon({
        // Replaces Leaflet's own class, which would draw a white box behind all of this.
        className: "tech-map-marker",
        html: techMarkerHtml(tech, options),
        iconSize: [size, height],
        // The tip of the tail is where they are. The old icons hung below their position with
        // the point at the top, which put everyone a marker's height north of the truth.
        iconAnchor: [size / 2, height],
        popupAnchor: [0, -height],
        tooltipAnchor: [0, -height]
    })
}

export function techMarkerHtml(tech: Technician, options?: TechMarkerOptions): string {
    const size = options?.size ?? 40
    const plain = options?.plain === true
    const height = plain ? size : size + tailHeight - tailOverlap
    const ring = Math.max(2, Math.round(size * (options?.selected ? 0.12 : 0.07)))
    const color = colorFor(tech)
    const photo = techPhotoUrl(tech)
    const dot = Math.max(10, Math.round(size * 0.28))

    // The ring's colour says what state they are in — white, blue while being picked, green
    // while the phone is reporting — and that is a class rather than an inline colour on
    // purpose: going live and going quiet then costs one class on the marker instead of
    // rebuilding its icon, which on a map of a thousand faces is the whole difference.
    const classes = ["tech-pin", options?.selected ? "is-selected" : "", plain ? "is-plain" : ""]
        .filter(Boolean)
        .join(" ")

    return `<div class="${classes}" style="position:relative;width:${size}px;height:${height}px;">`
        + (plain
            ? ""
            : `<div class="tech-pin-tail" style="position:absolute;left:50%;bottom:0;`
                + `margin-left:${-tailWidth}px;width:0;height:0;`
                + `border-left:${tailWidth}px solid transparent;border-right:${tailWidth}px solid transparent;`
                + `border-top-width:${tailHeight}px;border-top-style:solid;`
                + `filter:drop-shadow(0 2px 2px rgba(0,0,0,.35));"></div>`)

        + `<div class="tech-pin-ring" style="position:absolute;top:0;left:0;width:${size}px;`
        + `height:${size}px;border-radius:50%;`
        + `${plain ? "" : "box-shadow:0 2px 6px rgba(0,0,0,.35);"}"></div>`

        // The initials sit underneath the photo rather than instead of it, so a picture that
        // fails to load — a moved file, a phone with no signal — falls back to something
        // recognisable instead of to a hole.
        + `<div style="position:absolute;top:${ring}px;left:${ring}px;width:${size - ring * 2}px;`
        + `height:${size - ring * 2}px;border-radius:50%;overflow:hidden;background:${color};`
        + `display:flex;align-items:center;justify-content:center;color:#fff;font-weight:700;`
        + `font-size:${Math.round(size * 0.36)}px;line-height:1;letter-spacing:.5px;">`
        + escape(initialsFor(tech))
        + (photo
            ? `<img src="${escape(photo)}" alt="" onerror="this.remove()" style="position:absolute;`
                + `top:0;left:0;width:100%;height:100%;object-fit:cover;" />`
            : "")
        + `</div>`

        // Reporting right now. Shown only while the marker wears the live class, so it appears
        // and disappears without the pin being rebuilt.
        + (plain
            ? ""
            : `<div class="tech-pin-live" style="position:absolute;right:0;top:0;width:${dot}px;`
                + `height:${dot}px;border-radius:50%;"></div>`)

        + (options?.busy
            ? `<div style="position:absolute;right:0;top:${size - dot}px;width:${dot}px;`
                + `height:${dot}px;border-radius:50%;background:#f1416c;border:2px solid #fff;"></div>`
            : "")

        + `</div>`
}

/** Two letters where there are two names, one where there is one. */
function initialsFor(tech: Technician): string {
    const name = [tech.firstName, tech.lastName]
        .filter(part => !window.isNullOrEmpty(part))
        .join(" ")
        .trim()
        || (tech.fullName ?? tech.username ?? "").trim()

    const words = name.split(/\s+/).filter(word => word.length > 0)

    if (words.length === 0) return "?"
    if (words.length === 1) return words[0].substring(0, 1).toUpperCase()

    return (words[0].substring(0, 1) + words[words.length - 1].substring(0, 1)).toUpperCase()
}

/** Theirs for good: the same person is the same colour on every map and every session. */
function colorFor(tech: Technician): string {
    const seed = tech.id ?? tech.username ?? tech.fullName ?? ""

    let hash = 0
    for (let i = 0; i < seed.length; i++) hash = (hash * 31 + seed.charCodeAt(i)) % 100000

    return palette[hash % palette.length]
}

/**
 * Where their picture actually is.
 *
 * An avatar arrives either absolute or as a path on the CDN, and a path used as-is is a broken
 * image — which is how the board's own tooltip has been showing them.
 */
export function techPhotoUrl(tech: { avatar?: string }): string | undefined {
    const avatar = tech.avatar

    if (window.isNullOrEmpty(avatar)) return undefined
    if (avatar === "/assets/media/avatars/blank.png") return undefined

    return avatar!.startsWith("http") ? avatar : Environment.instance.hubUrl + avatar
}

/** The marker is built as markup, and a name is somebody else's text. */
function escape(value: string): string {
    return value
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;")
}
